import {
    Component,
    createEffect,
    createMemo,
    createSignal,
    For,
    JSX,
    on,
    Show,
} from 'solid-js'

import { useSearchParams } from '@solidjs/router'
import {
    api_admin_users_del,
    api_admin_users_list,
    api_admin_users_patch,
    api_admin_users_search_list,
    api_companies_users_count_get,
    api_companies_users_del,
    api_companies_users_list,
    api_companies_users_patch,
    api_companies_users_search_list,
    StaffRole,
    User,
} from 'abi'
import {
    addAlert,
    Counter,
    LoadingElem,
    Pagination,
    SearchInp,
    setPopup,
} from 'components'
import { AddBtn } from 'components/addBtn'
import {
    BanIcon,
    CheckFillIcon,
    CloseIcon,
    EditIcon,
    LogoutIcon,
    StaffIcon,
    UserIcon,
    UsersIcon,
} from 'icons'
import { outletRef } from 'pages/panel'
import { PROVINCE_FA } from 'pages/panel/profile'
import { createStore, produce, unwrap } from 'solid-js/store'
import { userRole } from 'store/self'
import { debounce } from 'utils/tools'
import {
    cState,
    isAdmin,
    isCorpManager,
    NoPermsForThisCorp,
    setCState,
} from '../shared'
import { AddUserCmp } from './add'
import './style/users.scss'

type STATE = {
    loading: boolean
    error: boolean

    add: {
        type: 'edit' | 'add'
        show: boolean
        user: User | null
    }

    query: string

    role: StaffRole | null

    page: number
    max_page: number

    users: User[]
}

const [state, setState] = createStore<STATE>({
    loading: true,
    error: false,

    users: [],

    query: '',

    role: null,

    add: {
        show: false,
        type: 'add',
        user: null,
    },

    page: 0,

    max_page: -1,
})

export { state as CorpUsersS }

const CorpUsers: Component = () => {
    const [search_params, setSearchParams] = useSearchParams()

    createEffect(
        on(
            () =>
                [
                    cState.corp?.id,
                    search_params.page,
                    state.query,
                    state.role,
                ] as const,
            ([id, p, query]) => {
                if (!id) return

                if (!isCorpManager(id) && !isAdmin()) {
                    addAlert({
                        type: 'error',
                        timeout: 3,
                        subject: 'خطا!',
                        content: 'شما دسترسی به این بخش را ندارید!',
                    })
                    return
                }

                setSearchParams({ page: parseInt(p || '0') })

                if (state.max_page !== -1) getCount(id)

                if (!p) return

                if (query) {
                    return debouncedSetSearch(query)
                }

                defaultSearch(parseInt(p))

                return
            }
        )
    )

    const getCount = async (cid: number) => {
        let res = await api_companies_users_count_get({ cid })

        if (!res.ok()) return

        setState('max_page', Math.max(Math.floor(res.body.total / 32), 0))
    }

    const defaultSearch = async (page: number) => {
        setState({ loading: true })

        let res

        if (isAdmin()) {
            res = await api_admin_users_list({
                page,
                company: cState.corp!.id,
                role: state.role,
            })
        } else {
            res = await api_companies_users_list({
                cid: cState.corp!.id,
                role: state.role,
                page,
                order: 'desc',
            })
        }

        if (res === undefined) return NoPermsForThisCorp()

        setState({ loading: false })

        if (!res.ok()) return setState({ error: true })

        setState({ users: res.body })
    }

    const debouncedSetSearch = debounce(async (query: string) => {
        setState({ loading: true, error: false })

        let page = unwrap(state.page)

        let res

        if (isAdmin()) {
            res = await api_admin_users_search_list({
                company: cState.corp!.id,
                page,
                username: `%${query}%`,
            })
        } else {
            res = await api_companies_users_search_list({
                cid: cState.corp!.id,
                username: `%${query}%`,
            })
        }

        if (state.query !== query) return

        setState({ loading: false })

        if (!res.ok()) return setState({ error: true })

        setState('users', res.body)
    }, 200)

    interface CountCmpProps {
        icon: JSX.Element
        holder: string
        value: number

        onclick(): void
        active: boolean
    }
    const CountCmp: Component<CountCmpProps> = C => {
        return (
            <div
                class='counter'
                role='button'
                onclick={C.onclick}
                classList={{ active: C.active }}
            >
                <div class='icon'>{C.icon}</div>
                <div class='value title_hero'>
                    <Counter end={C.value} />
                </div>
                <p class='holder title_smaller'>{C.holder}</p>
            </div>
        )
    }

    const staffsC = createMemo(() => cState.corp?.manager_count || 0)
    const agentsC = createMemo(() => cState.corp?.agent_count || 0)

    const dltUser = (id: number) => {
        setState(
            produce(s => {
                let idx = s.users.findIndex(u => u.id == id)

                if (id == -1) return

                let name = s.users[idx]?.name
                addAlert({
                    type: 'success',
                    subject: 'اخراج موفق!',
                    timeout: 3,
                    content: `کاربر ${name} با موفقیت اخراج شد.`,
                })
                s.users.splice(idx, 1)
            })
        )
    }
    const toggleUserBan = (idx: number, ban: boolean) => {
        setState(
            produce(s => {
                let id = s.users.findIndex(f => f.id == idx)
                if (id == -1) return
                s.users[id]!.banned = ban
            })
        )
    }

    return (
        <div class='corp-section corp-users'>
            <div class='counters'>
                <CountCmp
                    holder='تعداد کل'
                    icon={<UsersIcon />}
                    value={staffsC() + agentsC()}
                    active={state.role == null}
                    onclick={() => {
                        setState('role', null)
                    }}
                />
                <CountCmp
                    holder='مدیرعامل'
                    icon={<StaffIcon />}
                    value={staffsC()}
                    active={state.role == 'manager'}
                    onclick={() => {
                        setState('role', 'manager')
                    }}
                />
                <CountCmp
                    holder='نماینده'
                    icon={<UserIcon />}
                    value={agentsC()}
                    active={state.role == 'agent'}
                    onclick={() => {
                        setState('role', 'agent')
                    }}
                />
            </div>

            <AddBtn
                onClick={() =>
                    setState('add', { show: true, user: null, type: 'add' })
                }
                class='title_smaller'
                title='اضافه به اعضا'
            />

            <SearchInp
                value={state.query}
                active={state.query.length !== 0}
                oninput={e => setState({ query: e.currentTarget.value.trim() })}
                onClear={() => setState({ query: '' })}
                id='corp-users-search-inp'
                class='users-search'
            />

            <div
                class='table-container'
                classList={{ min: state.users?.length == 0 || state.error }}
            >
                <table>
                    <thead>
                        <tr class='title_small'>
                            <th>
                                <span>نام</span>
                            </th>
                            <th>
                                <span>نام کاربری</span>
                            </th>
                            <th>
                                <span>استان</span>
                            </th>
                            <th>
                                <span>نوع دسترسی</span>
                            </th>
                            <th class='date'>
                                <span>تاریخ ثبت</span>
                            </th>
                            <th>
                                <span>تغییر</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <Show when={!state.loading} fallback={<Loading />}>
                            <Show
                                when={state.users.length > 0 && !state.error}
                                fallback={
                                    <tr class='table-empty title_hero'>
                                        <td colspan='10'>
                                            <div class='icon'>
                                                <CloseIcon />
                                            </div>
                                            <div>
                                                اطلاعاتی برای نمایش وجود ندارد!
                                            </div>
                                        </td>
                                    </tr>
                                }
                            >
                                <For each={state.users}>
                                    {u => (
                                        <TableRow
                                            user={u}
                                            page={state.page}
                                            onDel={() => dltUser(u.id)}
                                            onBan={ban => {
                                                toggleUserBan(u.id, ban)
                                            }}
                                            onEdit={() =>
                                                setState('add', {
                                                    type: 'edit',
                                                    user: { ...u },
                                                    show: true,
                                                })
                                            }
                                        />
                                    )}
                                </For>
                            </Show>
                        </Show>
                    </tbody>
                </table>
            </div>

            <Show when={state.max_page > 0}>
                <Pagination
                    max_page={state.max_page}
                    current={state.page || 0}
                    onChange={page => {
                        setSearchParams({ page })
                        outletRef?.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        })
                    }}
                />
            </Show>

            <AddUserCmp
                add={state.add}
                onClose={() =>
                    setState('add', { type: 'add', user: null, show: false })
                }
                onAdd={c => {
                    setState(
                        produce(s => {
                            s.users.unshift(c)
                        })
                    )
                    setCState(
                        produce(s => {
                            if (c.staff_role == 'manager') {
                                s.corp!.manager_count += 1
                            } else {
                                s.corp!.agent_count += 1
                            }
                        })
                    )
                }}
                onEdit={c => {
                    setState(
                        produce(s => {
                            let id = s.users.findIndex(u => u.id == c.id)

                            if (id != -1) {
                                s.users[id] = c
                            }
                        })
                    )
                    setCState(
                        produce(s => {
                            if (c.staff_role == 'manager') {
                                s.corp!.manager_count += 1

                                if (s.corp!.agent_count !== 0) {
                                    s.corp!.agent_count -= 1
                                }
                            } else {
                                s.corp!.agent_count += 1

                                if (s.corp!.manager_count !== 0) {
                                    s.corp!.manager_count -= 1
                                }
                            }
                        })
                    )
                }}
            />
        </div>
    )
}

type TableRowProps = {
    user: User
    page: number

    onDel(): void
    onEdit(): void
    onBan(ban: boolean): void
}
export const TableRow: Component<TableRowProps> = P => {
    const [loading, setLoading] = createSignal(false)

    const fireUser = async (id: number) => {
        setLoading(true)

        let res
        if (isAdmin()) {
            res = await api_admin_users_del({ id })
        } else {
            res = await api_companies_users_del({
                cid: cState.corp!.id,
                uid: id,
            })
        }

        if (!res.ok()) return

        setLoading(true)

        P.onDel()
    }

    const UnbanBtnCmp = () => {
        const unbanUser = async () => {
            setLoading(true)

            let res

            if (isAdmin()) {
                res = await api_admin_users_patch(
                    {
                        id: P.user.id,
                    },
                    {
                        name: P.user.name,
                        username: P.user.username,
                        password: null,
                        banned: false,
                        staff_at: P.user.staff_at,
                        staff_role: P.user.staff_role,
                        province: P.user.province,
                    },
                    {
                        show_alert: true,
                    }
                )
            }
            if (isCorpManager(cState.corp!.id)) {
                res = await api_companies_users_patch(
                    {
                        cid: cState.corp!.id,
                        uid: P.user.id,
                    },
                    {
                        name: P.user.name,
                        username: P.user.username,
                        password: null,
                        banned: false,
                        province: P.user.province,
                    },
                    {
                        show_alert: true,
                    }
                )
            }

            if (res === undefined) return NoPermsForThisCorp()

            setLoading(false)

            if (!res.ok()) return

            let name = P.user.name

            addAlert({
                type: 'success',
                timeout: 3,
                subject: 'موفق!',
                content: `کاربر ${name} با موفقیت آنبن شد.`,
            })

            P.onBan(false)
        }
        return (
            <button
                class='green icon'
                onclick={e => {
                    e.stopPropagation()
                    setPopup({
                        show: true,
                        type: 'success',
                        timer: 2,
                        Icon: CheckFillIcon,
                        title: 'کاربر را آنبن میکنید؟',
                        content:
                            'بعد از آنبن تمام دسترسی های کاربر به سایت بر میگردد.',
                        onSubmit() {
                            unbanUser()
                        },
                    })
                }}
            >
                <CheckFillIcon />
            </button>
        )
    }
    const BanBtnCmp = () => {
        const banUser = async () => {
            setLoading(true)

            let res
            if (isAdmin()) {
                res = await api_admin_users_patch(
                    {
                        id: P.user.id,
                    },
                    {
                        name: P.user.name,
                        username: P.user.username,
                        password: null,
                        banned: true,
                        staff_at: P.user.staff_at,
                        staff_role: P.user.staff_role,
                        province: P.user.province,
                    },
                    {
                        show_alert: true,
                    }
                )
            }
            if (isCorpManager(cState.corp!.id)) {
                res = await api_companies_users_patch(
                    {
                        cid: cState.corp!.id,
                        uid: P.user.id,
                    },
                    {
                        name: P.user.name,
                        username: P.user.username,
                        password: null,
                        banned: true,
                        province: P.user.province,
                    },
                    {
                        show_alert: true,
                    }
                )
            }

            if (res === undefined) return NoPermsForThisCorp()

            setLoading(false)

            if (!res.ok()) return

            let name = P.user.name

            addAlert({
                type: 'success',
                timeout: 3,
                subject: 'موفق!',
                content: `کاربر ${name} با موفقیت بن شد.`,
            })
            P.onBan(true)
        }
        return (
            <button
                class='red icon'
                onclick={e => {
                    e.stopPropagation()
                    setPopup({
                        show: true,
                        type: 'error',
                        timer: 3,
                        Icon: BanIcon,
                        title: 'کاربر را بن میکنید؟',
                        content:
                            'بعد از بن کاربر هیچ دسترسی ای به سایت نخواهد داشت!',
                        onSubmit() {
                            banUser()
                        },
                    })
                }}
            >
                <BanIcon />
            </button>
        )
    }

    return (
        <tr class='title_smaller' onclick={() => P.onEdit()}>
            <Show
                when={!loading()}
                fallback={
                    <td class='loading-tr' colspan={6} aria-hidden='true'>
                        <LoadingElem />
                    </td>
                }
            >
                <td>
                    <div class='name' title={P.user.banned ? 'بن شده' : ''}>
                        <div
                            class='ban-icon icon'
                            classList={{ active: P.user.banned }}
                        >
                            <BanIcon />
                        </div>
                        {P.user.name || 'N / A'}
                    </div>
                </td>
            </Show>

            <td>{P.user.username || 'N / A'}</td>

            <td>{PROVINCE_FA[P.user.province]}</td>

            <td>{userRole(P.user)}</td>

            <td class='date'>
                {new Date(P.user.created_at * 1e3).toLocaleDateString('fa-IR')}
            </td>
            <td>
                <div class='ctas'>
                    <Show when={!P.user.banned} fallback={<UnbanBtnCmp />}>
                        <BanBtnCmp />
                    </Show>

                    <button
                        class='yellow icon'
                        onclick={e => {
                            e.stopPropagation()
                            setPopup({
                                show: true,
                                title: 'اخراج از شرکت؟',
                                timer: 2,
                                type: 'error',
                                Icon: LogoutIcon,
                                content: 'این عمل غیر قابل بازگشت است.',
                                onSubmit() {
                                    fireUser(P.user.id)
                                },
                            })
                        }}
                    >
                        <LogoutIcon />
                    </button>
                    <button class='blue icon'>
                        <EditIcon />
                    </button>
                </div>
            </td>
        </tr>
    )
}

const Loading = () => {
    return (
        <tr class='table-loading title_hero'>
            <td colspan='10'>
                <img src='/public/imgs/logo.png' decoding='async' alt='' />
                <div>در حال بارگذاری...</div>
            </td>
        </tr>
    )
}

export { setState as setUState, state as Ustate, type STATE as USTATE }

export default CorpUsers
